import React, { FC } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import { LightTooltip } from '~/components/UI/LightTooltip/LightTooltip';
import { BodyText } from '~/components/UI/Texts';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { IUseVideoReview } from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { CustomThumbDownIcon, CustomThumbUpIcon } from '~/pages/SingleVideoPage/styles';
import { SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

interface ThumbBlockProps {
  rating?: number | null;
  thumbSelected?: boolean;
  likedVideo?: boolean;
  leaveReview?: IUseVideoReview['leaveReview'];
  setLikedOutsideCommentSection?: IUseVideoReview['setLikedOutsideCommentSection'];
  handleOpenComments?: () => void;
}

const ThumbBlock: FC<ThumbBlockProps> = ({
  rating,
  thumbSelected,
  leaveReview,
  handleOpenComments,
  likedVideo,
  setLikedOutsideCommentSection,
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const handleLeaveReview = (liked: boolean) => {
    setLikedOutsideCommentSection?.(true);
    leaveReview?.({ liked });
    handleOpenComments?.();
  };

  if (thumbSelected !== undefined) {
    return (
      <LightTooltip title={t('page.video.thanksForReview')} placement="bottom">
        <Wrapper data-testid="thumbBlock">
          <LeftBlock>
            <CustomThumbUpIcon
              data-testid={thumbSelected ? 'blueThumbUpActive' : 'blueThumbUp'}
              filled={thumbSelected}
              color={theme.palette.common.accentFaded}
            />
            {Boolean(rating) && <StyledText>{rating}%</StyledText>}
          </LeftBlock>
          <StyledThumbDownIcon
            data-testid={!thumbSelected ? 'blueThumbDownActive' : 'blueThumbDown'}
            filled={!thumbSelected}
            color={theme.palette.common.accentFaded}
          />
        </Wrapper>
      </LightTooltip>
    );
  }

  return (
    <Wrapper data-testid="thumbBlock">
      <LeftBlock>
        <LightTooltip title={t('page.video.highlySatisfied')} placement="bottom">
          <IconButton
            data-testid="thumbUpTitleIcon"
            onClick={() => {
              sendAnalyticEvent(VIDEO_PAGE_EVENTS.thumbUp);
              handleLeaveReview(true);
            }}
          >
            <CustomThumbUpIcon
              filled={likedVideo === true}
              color={theme.palette.common.primary}
              animated={true}
            />
          </IconButton>
        </LightTooltip>
        {Boolean(rating) && <StyledText>{rating}%</StyledText>}
      </LeftBlock>
      <LightTooltip title={t('page.video.dissatisfied')} placement="bottom">
        <IconButton
          data-testid="thumbDownTitleIcon"
          onClick={() => {
            sendAnalyticEvent(VIDEO_PAGE_EVENTS.thumbDown);
            handleLeaveReview(false);
          }}
        >
          <StyledThumbDownIcon filled={likedVideo === false} color={theme.palette.common.primary} />
        </IconButton>
      </LightTooltip>
    </Wrapper>
  );
};

export default ThumbBlock;

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
`;
const LeftBlock = styled('div')`
  display: flex;
  align-items: center;
`;
const IconButton = styled('button')`
  display: flex;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;
const StyledThumbDownIcon = styled(CustomThumbDownIcon)`
  margin-left: ${SMALL_MARGIN_PX};
`;
const StyledText = styled(BodyText)`
  margin-left: ${TINY_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.white};
  font-weight: bold;
  && {
    line-height: 1.25;
  }
`;
